/* ControlHomePage — §17-29 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { controlApi, useControlStream } from "../lib/controlApi";
import { STATUS_STYLE, OVERALL_MSG } from "../ControlApp";
import { RefreshCw, Loader2, CheckCircle2, AlertTriangle, XCircle, PauseCircle, Zap, Facebook, Instagram, Globe, Bot, Clock, Wifi, WifiOff, Sparkles } from "lucide-react";

const OVERALL_ICON = {
  operational: CheckCircle2, degraded: AlertTriangle, critical: XCircle,
  paused: PauseCircle, unknown: AlertTriangle,
};

function StatusChip({ status, label }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.unknown;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold border ${s.border} ${s.bg} ${s.text}`}>
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {label || s.label}
    </span>
  );
}

function ArticleChips({ art }) {
  const chip = (label, st) => {
    let color = "bg-slate-100 text-slate-500";
    if (st === "published" || st === "test_mode") color = "bg-emerald-100 text-emerald-700";
    else if (st === "queued" || st === "publishing" || st === "retrying") color = "bg-amber-100 text-amber-700";
    else if (st === "failed" || st === "disconnected" || st === "expired" || st === "cancelled") color = "bg-red-100 text-red-700";
    const symbol = st === "published" || st === "test_mode" ? "✓" : (st === "failed" || st === "disconnected") ? "⚠" : "—";
    return <span className={`px-1.5 py-0.5 text-[10px] font-bold ${color}`}>{label} {symbol}</span>;
  };
  return (
    <div className="flex gap-1 flex-wrap">
      {chip("WEB", art.portal_status)}
      {chip("FB", art.facebook_status)}
      {chip("IG", art.instagram_status)}
    </div>
  );
}

export default function ControlHomePage() {
  const [data, setData] = useState(null);
  const [recent, setRecent] = useState([]);
  const [nextRun, setNextRun] = useState(null);
  const [topSuggestion, setTopSuggestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);

  const load = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const [d, arts, nr, learn] = await Promise.all([
        controlApi.dashboard(),
        controlApi.articles({ page: 1, page_size: 8, sort: "published_at:desc" }),
        controlApi.nextRun(),
        controlApi.learning(30).catch(() => null),
      ]);
      setData(d); setRecent(arts.items || []); setNextRun(nr);
      const pending = (learn && learn.top_suggestions) || [];
      setTopSuggestion(pending[0] || null);
      setLastUpdate(new Date());
    } catch (_e) {
      // stale-while-offline via SW cache
    } finally { setLoading(false); setRefreshing(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  const connected = useControlStream((type) => {
    if (["automation.changed", "article.removed", "source.updated",
          "config.changed", "incident.acknowledged"].includes(type)) {
      load(true);
    }
  });
  // Polling fallback every 30s (§19, §105).
  useEffect(() => {
    const t = setInterval(() => load(true), 30000);
    return () => clearInterval(t);
  }, [load]);

  if (loading && !data) {
    return <div className="p-6 text-textgray flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>;
  }
  const overall = (data && data.system && data.system.status) || "unknown";
  const OverallIcon = OVERALL_ICON[overall] || AlertTriangle;
  const om = OVERALL_MSG[overall] || OVERALL_MSG.unknown;
  const oc = STATUS_STYLE[overall] || STATUS_STYLE.unknown;
  const channels = (data && data.channels) || {};
  const today = (data && data.today) || {};
  const automation = (data && data.automation) || {};

  return (
    <div className="p-4 space-y-4" data-testid="spc-home">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.35em] text-textgray">Sur Primicia</div>
          <div className="font-serif text-2xl font-bold text-blueblack">CONTROL</div>
        </div>
        <button onClick={() => load(true)} disabled={refreshing} data-testid="spc-refresh"
          className="w-10 h-10 flex items-center justify-center border border-softblue bg-white">
          {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </button>
      </div>
      <div className="text-[10px] text-textgray flex items-center gap-1.5">
        {connected ? <Wifi className="w-3 h-3 text-emerald-600" /> : <WifiOff className="w-3 h-3" />}
        {connected ? "Actualizado en tiempo real" : "Modo sincronización periódica"}
        {lastUpdate && ` · ${lastUpdate.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })}`}
      </div>

      {/* Overall */}
      <div data-testid="spc-overall" className={`p-4 border ${oc.border} ${oc.bg} flex items-start gap-3`}>
        <OverallIcon className={`w-8 h-8 ${oc.text} shrink-0`} />
        <div className="flex-1">
          <div className={`font-serif text-lg font-bold ${oc.text}`}>{om.title}</div>
          <div className="text-xs text-textgray">{om.sub}</div>
        </div>
      </div>

      {/* Next Run (§21) */}
      {nextRun && (
        <Link to="/control/more/runs" className="block border border-softblue bg-white p-4" data-testid="spc-next-run">
          <div className="flex items-center justify-between mb-1">
            <div className="text-[10px] uppercase tracking-widest text-textgray font-bold">Próxima tanda</div>
            <Clock className="w-4 h-4 text-textgray" />
          </div>
          <div className="flex items-baseline gap-3">
            <div className="font-serif text-3xl font-bold text-blueblack">
              {nextRun.type === "morning" ? "08:00" : nextRun.type === "afternoon" ? "15:00" : "20:00"}
            </div>
            <div className="text-xs text-textgray">
              En {Math.floor(nextRun.in_minutes / 60)}h {nextRun.in_minutes % 60}min
            </div>
          </div>
          {nextRun.provisional_candidates > 0 && (
            <div className="text-[11px] text-textgray mt-1">
              ~{nextRun.provisional_candidates} candidatas elegibles
            </div>
          )}
        </Link>
      )}

      {/* Canales (§23) */}
      <div className="grid grid-cols-2 gap-2">
        <ChannelCard icon={Globe} name="PORTAL" status={channels.portal} />
        <ChannelCard icon={Facebook} name="FACEBOOK" status={channels.facebook} />
        <ChannelCard icon={Instagram} name="INSTAGRAM" status={channels.instagram} />
        <ChannelCard icon={Bot} name="MOTOR" status={
          automation.mode === "paused" ? "paused" :
          overall === "critical" ? "critical" : "operational"
        } />
      </div>

      {/* Hoy (§26) */}
      <div className="border border-softblue bg-white">
        <div className="px-4 py-2 border-b border-softblue text-[10px] uppercase tracking-widest text-textgray font-bold">Hoy</div>
        <div className="grid grid-cols-4 divide-x divide-softblue text-center">
          {[
            ["Portal", today.portal || 0],
            ["Facebook", today.facebook || 0],
            ["Instagram", today.instagram || 0],
            ["Urgentes", today.urgent || 0, true],
          ].map(([label, val, urgent]) => (
            <div key={label} className="p-3">
              <div className={`font-serif text-2xl font-bold ${urgent && val > 0 ? "text-red-600" : "text-blueblack"}`}>{val}</div>
              <div className="text-[10px] uppercase tracking-widest text-textgray">{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Publicaciones recientes (§28) */}
      <div className="border border-softblue bg-white">
        <div className="px-4 py-2 border-b border-softblue text-[10px] uppercase tracking-widest text-textgray font-bold flex items-center justify-between">
          <span>Publicaciones recientes</span>
          <Link to="/control/articles" className="text-nightblue underline">Ver todas</Link>
        </div>
        {recent.length === 0 ? (
          <div className="p-6 text-center text-textgray text-xs flex flex-col items-center gap-1">
            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
            Sin publicaciones aún hoy.
          </div>
        ) : (
          <ul className="divide-y divide-softblue">
            {recent.slice(0, 6).map((art) => (
              <li key={art.id}>
                <Link to={`/control/articles/${art.id}`} className="flex items-start gap-3 p-3">
                  {art.image_url ? (
                    <img src={art.image_url} alt="" loading="lazy"
                      className="w-14 h-14 object-cover shrink-0 bg-slate-100" />
                  ) : (
                    <div className="w-14 h-14 shrink-0 bg-slate-100 border border-softblue" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] text-textgray uppercase tracking-widest flex items-center gap-1">
                      {art.published_at ? new Date(art.published_at).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" }) : "—"}
                      {art.urgent && <><Zap className="w-3 h-3 text-red-500 ml-1" /><span className="text-red-600 font-bold">Urgente</span></>}
                    </div>
                    <div className="font-serif text-sm font-bold text-blueblack line-clamp-2">{art.title}</div>
                    <div className="mt-1"><ArticleChips art={art} /></div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      {data && data.open_incidents > 0 && (
        <Link to="/control/alerts" className="block border border-red-300 bg-red-50 p-3 text-red-800 text-sm font-semibold flex items-center justify-between">
          <span>⚠ {data.open_incidents} alerta{data.open_incidents > 1 ? "s" : ""} abierta{data.open_incidents > 1 ? "s" : ""}</span>
          <span className="text-xs underline">Ver</span>
        </Link>
      )}

      {/* §36 — hint editorial discreto */}
      {topSuggestion && (
        <Link to="/control/more/learning" data-testid="spc-suggestion-hint"
          className="block border border-cyan-300 bg-cyan-50 p-3 text-cyan-900 text-xs flex items-start gap-2">
          <Sparkles className="w-4 h-4 text-cyan-700 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-bold">💡 Sugerencia editorial</div>
            <div className="mt-0.5">{topSuggestion.human_reason}</div>
          </div>
          <span className="underline shrink-0">Ver</span>
        </Link>
      )}
    </div>
  );
}

function ChannelCard({ icon: Icon, name, status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.unknown;
  return (
    <div data-testid={`spc-channel-${name.toLowerCase()}`}
      className={`border ${s.border} ${s.bg} p-3 flex items-center gap-3`}>
      <Icon className={`w-5 h-5 ${s.text}`} />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-textgray font-bold">{name}</div>
        <div className={`text-xs font-bold ${s.text}`}>{s.label}</div>
      </div>
    </div>
  );
}
